'use client';

import type { PlanTier } from './PlansComparison';
import { useTranslations } from 'next-intl';
import { Button } from '@/app/components/ui/Button';
import Modal from '@/app/components/ui/Modal';
import { Separator } from '@/app/components/ui/Separator';
import { cn } from '@/app/lib/utils';
import dynamic from 'next/dynamic';
import { ArrowRight, X } from 'lucide-react';

const UpgradePlanCheckoutElements = dynamic(() => import('./UpgradePlanCheckoutElements'), {
  ssr: false,
});

type UpgradePlanModalProps = {
  clientId: string;
  currentPlan: PlanTier;
  selectedPlan: PlanTier;
  isDowngrade?: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  zIndex?: number;
};

function formatPlanTitle(name: string) {
  const normalized = name.trim();
  if (/plan$/i.test(normalized)) {
    return normalized.replace(/plan$/i, 'plan');
  }

  return `${normalized} plan`;
}

function formatPlanPeriod(period: string) {
  if (period === '/monthly') return '/month';
  if (period === '/yearly') return '/year';
  return period;
}

function PlanSummaryCard({
  badgeLabel,
  badgeClassName,
  plan,
  isHighlighted = false,
  contentClassName,
  helperText,
}: {
  badgeLabel: string;
  badgeClassName: string;
  plan: PlanTier;
  isHighlighted?: boolean;
  contentClassName?: string;
  helperText?: string;
}) {
  const formattedName = formatPlanTitle(plan.name);
  const formattedPeriod = formatPlanPeriod(plan.period);

  return (
    <div
      className={cn(
        'flex min-h-[146px] flex-1 flex-col items-start justify-start rounded-[4px] border border-border bg-background p-5 shadow-[0px_1px_2px_0px_rgba(10,13,20,0.03)]',
        isHighlighted && 'border-information-base bg-primary/10'
      )}
    >
      <div className={cn('flex flex-col items-start gap-1', contentClassName)}>
        <div
          className={cn(
            'inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-medium uppercase leading-3 tracking-[0.22px]',
            badgeClassName
          )}
        >
          {badgeLabel}
        </div>

        <p className="text-[14px] font-medium leading-5 tracking-[-0.084px] text-foreground">
          {formattedName}
        </p>

        <div className="flex flex-col items-start gap-2">
          <div className="flex items-center gap-0.5 leading-none">
            <span className="text-[24px] font-medium leading-8 text-foreground">{plan.price}</span>
            <span className="mt-[6px] text-[12px] leading-4 text-muted-foreground">
              {formattedPeriod}
            </span>
          </div>

          {plan.creditsLabel ? (
            <div className="inline-flex items-center rounded-[4px] border border-borderbg-background px-2 py-1 text-[12px] font-medium leading-4 text-muted-foreground shadow-[0px_1px_2px_0px_rgba(10,13,20,0.03)]">
              {plan.creditsLabel}
            </div>
          ) : null}

          {helperText ? (
            <p className="text-[12px] leading-4 tracking-[-0.072px] text-muted-foreground">
              {helperText}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function UpgradePlanModal({
  clientId,
  currentPlan,
  selectedPlan,
  isDowngrade = false,
  isOpen,
  onOpenChange,
  onSuccess,
  zIndex,
}: UpgradePlanModalProps) {
  const t = useTranslations('settings.plansPage');

  const handleCheckoutComplete = () => {
    onOpenChange(false);
    onSuccess?.();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        zIndex={zIndex}
        bodyClassName={cn(
          'gap-0 overflow-hidden rounded-[4px] border border-border bg-background p-0 shadow-[0px_0px_0px_0px_rgba(51,51,51,0.04),0px_1px_1px_0px_rgba(51,51,51,0.04),0px_3px_3px_0px_rgba(51,51,51,0.02),0px_6px_6px_0px_rgba(51,51,51,0.04),0px_12px_12px_0px_rgba(51,51,51,0.04),0px_24px_24px_0px_rgba(51,51,51,0.04),0px_48px_48px_0px_rgba(51,51,51,0.04)]',
          'max-w-[708px] sm:max-w-[708px]'
        )}
      >
        <div className="flex flex-row items-center justify-between border-b border-border px-5 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <div className="min-w-0">
              <h2 className="text-[18px] font-medium leading-6 tracking-[-0.27px] text-foreground">
                {isDowngrade ? t('downgradePlanTitle') : t('upgradePlanTitle')}
              </h2>
              <p className="sr-only">
                {isDowngrade ? t('downgradePlanDescription') : t('upgradePlanDescription')}
              </p>
            </div>
          </div>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded-[4px] text-muted-foreground"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            <X className="h-5 w-5" />
            <span className="sr-only">{t('close')}</span>
          </Button>
        </div>

        <div className="max-h-[calc(100vh-96px)] overflow-y-auto px-6 py-6">
          <div className="flex flex-col gap-6">
            <div className="flex items-stretch gap-4">
              <PlanSummaryCard
                badgeLabel={t('currentPlanBadge')}
                badgeClassName="bg-success-light text-success-dark"
                plan={currentPlan}
                contentClassName="w-[174px]"
              />

              <div className="flex w-6 shrink-0 items-center justify-center text-foreground">
                <ArrowRight className="h-5 w-5" />
              </div>

              <PlanSummaryCard
                badgeLabel={t('newPlanBadge')}
                badgeClassName="bg-information-light text-information-dark"
                plan={selectedPlan}
                isHighlighted
                contentClassName="min-w-0"
                helperText={t('planStartsImmediately')}
              />
            </div>

            <Separator className="bg-border" />

            <div className="flex flex-col gap-4">
              <h3 className="text-[18px] font-medium leading-6 tracking-[-0.27px] text-foreground">
                {t('billingDetails')}
              </h3>

              <Separator className="bg-border" />

              {isOpen ? (
                <UpgradePlanCheckoutElements
                  clientId={clientId}
                  planId={selectedPlan.id}
                  onComplete={handleCheckoutComplete}
                />
              ) : null}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
